/**
 * Chat 域 — React Hooks
 *
 * 提供 mock 聊天（含打字机效果模拟）和真实 SSE 流式回复。
 * 后端 SSE 事件格式：{type: "text", data: "..."} | {type: "done", data: {...}}
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { shouldUseMock } from "../../api/config";
import {
  chatApi,
  normalizeAgentRunMeta,
  type AgentRunMeta,
  type AgentFeedbackRating,
  type AgentRewriteTarget,
  type AgentRememberScope,
  type ChatMessage,
  type Attachment,
} from "./api";
import { useChatMessageStore } from "../../stores/chat-message-store";
import { useSampleDataStore } from "../../stores/sample-data-store";

const STREAM_ERROR_TEXT = "抱歉，AI 顾问暂时不可用，请稍后重试。";
const EMPTY_STREAM_TEXT = "AI 顾问暂时没有生成回复，请稍后重试。";
const MERGED_ERROR_CODE = "MERGED";
const RECENT_LOCAL_MESSAGE_MS = 5 * 60 * 1000;
const THINKING_STATUS_LABELS: Record<string, string> = {
  accepted: "正在准备回答",
  answer_done: "正在整理回答",
  classified: "正在理解问题",
  completed: "正在整理档案依据",
  denied: "暂时无法处理",
  merged: "正在整理你的问题",
  parsed: "附件解析完成",
  parsing: "正在解析附件",
  progress: "正在思考",
  queued_next: "正在等待上一轮回答完成",
  received: "已收到附件",
  skipped: "正在生成回复",
  unsupported: "暂不支持这个附件类型",
  vision_reading: "正在查看图片",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readStringField(value: unknown, key: string): string | undefined {
  if (!isRecord(value)) return undefined;
  const field = value[key];
  return typeof field === "string" && field.trim() ? field : undefined;
}

function readStreamErrorCode(data: unknown): string | undefined {
  return readStringField(data, "code") ?? readStringField(data, "errorCode");
}

function readStreamErrorMessage(data: unknown): string {
  return (
    readStringField(data, "message") ??
    readStringField(data, "error") ??
    readStringField(data, "detail") ??
    readStringField(isRecord(data) ? data.data : undefined, "message") ??
    readStringField(isRecord(data) ? data.data : undefined, "error") ??
    "AgentCore FC stream error"
  );
}

function formatThinkingStatus(status: unknown): string | undefined {
  if (typeof status !== "string") return undefined;
  const trimmed = status.trim();
  if (!trimmed) return undefined;
  return THINKING_STATUS_LABELS[trimmed] ?? trimmed;
}

function nowIso(): string {
  return new Date().toISOString();
}

function isRecentLocalMessage(message: ChatMessage, now: number): boolean {
  if (!message.createdAt) return false;
  const parsed = Date.parse(message.createdAt);
  return Number.isFinite(parsed) && now - parsed <= RECENT_LOCAL_MESSAGE_MS;
}

function shouldClearSampleDataForSavedData(toast: DataSavedToast): boolean {
  const dataType = toast.dataType.toLowerCase();
  const key = toast.key.toLowerCase();
  return (
    dataType === "fhir" ||
    dataType === "report" ||
    dataType === "diagnostic_report" ||
    dataType.includes("report") ||
    key.includes("report")
  );
}

function readDoneMeta(data: unknown): AgentRunMeta | undefined {
  if (!isRecord(data)) return undefined;
  return normalizeAgentRunMeta(data.meta);
}

function containsReportTaskMessage(value: string | undefined): boolean {
  if (!value) return false;
  return value.includes("报告解析任务") || value.includes("体检报告");
}

function hasReportTaskId(record: Record<string, unknown>): boolean {
  return Boolean(
    readStringField(record, "taskId") ||
    readStringField(record, "task_id") ||
    readStringField(record, "reportTaskId") ||
    readStringField(record, "report_task_id"),
  );
}

function hasReportTaskMarker(record: Record<string, unknown>): boolean {
  const type = readStringField(record, "type")?.toLowerCase();
  const name = (
    readStringField(record, "name") ??
    readStringField(record, "toolName") ??
    readStringField(record, "tool_name")
  )?.toLowerCase();
  return (
    Boolean(readStringField(record, "reportTaskId")) ||
    Boolean(readStringField(record, "report_task_id")) ||
    type === "report_task" ||
    type === "reporttaskcreated" ||
    type === "report_task_created" ||
    name === "parse_medical_report" ||
    Boolean(record.saveMode) ||
    Boolean(record.attachmentId) ||
    Boolean(record.attachmentIds) ||
    containsReportTaskMessage(readStringField(record, "message"))
  );
}

function hasReportTaskIdDeep(value: unknown, depth = 0): boolean {
  if (depth > 4 || !isRecord(value)) return false;
  if (hasReportTaskId(value)) return true;
  return [
    value.data,
    value.result,
    value.toolResult,
    value.tool_result,
    value.meta,
    value.metadata,
    value.extensions,
    value.payload,
  ].some((entry) => hasReportTaskIdDeep(entry, depth + 1));
}

function hasReportTaskSignal(value: unknown, depth = 0): boolean {
  if (depth > 4 || !isRecord(value)) return false;
  if (hasReportTaskId(value) && hasReportTaskMarker(value)) return true;
  if (hasReportTaskMarker(value) && hasReportTaskIdDeep(value)) return true;

  return [
    value.data,
    value.result,
    value.toolResult,
    value.tool_result,
    value.meta,
    value.metadata,
    value.extensions,
    value.payload,
  ].some((entry) => hasReportTaskSignal(entry, depth + 1));
}

// ── DataSaved toast state ──
export interface DataSavedToast {
  dataType: string;
  key: string;
  detail?: string;
}

export const DATA_SAVED_MESSAGES: Record<string, string> = {
  fhir: "已记录到健康档案",
  memory: "已保存偏好记忆",
};

// ── Mock AI 回复 ──

const AI_RESPONSES: Record<string, string> = {
  "我的睡眠怎么样？":
    "根据你最近 7 天的数据，睡眠评分为 72/100。深睡眠比例偏低（仅 14%，建议达到 20%+）。建议：睡前 2 小时避免蓝光暴露，卧室温度保持 18-20°C，考虑补充镁元素（200-400mg 甘氨酸镁）。",
  "如何改善代谢？":
    "你的代谢维度评分 68/100。核心改善点：\n\n• 空腹血糖 5.8 mmol/L（偏高）→ 建议限制精制碳水\n• 甘油三酯 2.3 mmol/L（偏高）→ 增加 Omega-3 摄入\n• 胰岛素敏感性 → 每周 3 次 HIIT 或力量训练\n\n建议先从 16:8 间歇性断食开始，这是改善代谢最直接的方式。",
  今日饮食建议:
    "基于你的代谢状态，今日建议：\n\n🌅 早餐（8:00-10:00）：牛油果 + 全麦吐司 + 2 个鸡蛋\n🌞 午餐：三文鱼沙拉 + 藜麦 + 橄榄油醋汁\n🌙 晚餐（18:00前）：清蒸鲈鱼 + 西兰花 + 糙米\n\n关键：控制精制碳水，增加优质蛋白和健康脂肪。",
  解读体检报告:
    "你最近一次体检（2024年12月，美年大健康）中，需要关注以下指标：\n\n🔴 甘油三酯 2.3 mmol/L — 偏高，需干预\n🟡 LDL胆固醇 3.2 mmol/L — 关注\n🟡 空腹血糖 5.8 mmol/L — 关注\n🟢 肝功能 — 正常\n🟢 肾功能 — 正常\n\n建议点击具体指标查看详细分析。",
};

function getDefaultReply(question: string): string {
  return (
    AI_RESPONSES[question] ??
    `关于"${question}"这个问题，我需要结合你的健康档案进行分析。建议你先上传最新的体检报告，以便我提供更精准的建议。`
  );
}

// ── Hooks ──

export function useChatSession(initialSessionId?: string | null) {
  const useMock = shouldUseMock("chat");
  const storeRef = useRef(useChatMessageStore.getState());

  const [messages, setMessagesState] = useState<ChatMessage[]>(() =>
    storeRef.current.getMessages(initialSessionId || null),
  );
  const [isTyping, setIsTypingState] = useState(() =>
    storeRef.current.getIsTyping(initialSessionId || null),
  );
  const [sessionId, setSessionId] = useState<string | null>(
    initialSessionId || null,
  );
  const [sessionQuickPrompts, setSessionQuickPromptsState] = useState<
    string[] | null
  >(() => storeRef.current.getQuickPrompts(initialSessionId || null));
  const [error, setErrorState] = useState<string | null>(() =>
    storeRef.current.getError(initialSessionId || null),
  );

  const [dataSavedToasts, setDataSavedToasts] = useState<DataSavedToast[]>([]);
  const markRealDataSeed = useSampleDataStore((s) => s.markRealDataSeed);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const replyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendingCountRef = useRef(0);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef(0);
  // 追踪正在进行的请求占位符 ID，防止 refreshHistory 误删 in-flight 占位
  const activeInvocationsRef = useRef<Set<string>>(new Set());

  // 组件挂载状态，防止异步回调在卸载后操作 state
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 用 ref 保持 sessionId 的最新值，避免 sendMessage 执行期间闭包捕获旧值
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // 用 ref 保持 messages 的最新值，供 fetchHistory 异步合并时使用
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // 同步 state 到 store 的辅助函数
  const setMessages = useCallback(
    (updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
      setMessagesState((prev) => {
        const next =
          typeof updater === "function"
            ? (updater as (prev: ChatMessage[]) => ChatMessage[])(prev)
            : updater;
        messagesRef.current = next;
        const sid = sessionIdRef.current;
        if (sid) {
          storeRef.current.setMessages(sid, next);
        }
        return next;
      });
    },
    [],
  );

  const setIsTyping = useCallback(
    (value: boolean) => {
      setIsTypingState(value);
      const sid = sessionIdRef.current;
      if (sid) {
        storeRef.current.setIsTyping(sid, value);
      }
    },
    [],
  );

  const setSessionQuickPrompts = useCallback(
    (value: string[] | null) => {
      setSessionQuickPromptsState(value);
      const sid = sessionIdRef.current;
      if (sid) {
        storeRef.current.setQuickPrompts(sid, value);
      }
    },
    [],
  );

  const setError = useCallback(
    (value: string | null) => {
      setErrorState(value);
      const sid = sessionIdRef.current;
      if (sid) {
        storeRef.current.setError(sid, value);
      }
    },
    [],
  );

  const resetSessionState = useCallback((targetSessionId?: string | null) => {
    const sid = targetSessionId || sessionIdRef.current;
    const welcomeMessages = storeRef.current.getMessages(null);

    if (sid) {
      storeRef.current.clearSession(sid);
      storeRef.current.setMessages(sid, welcomeMessages);
      storeRef.current.setIsTyping(sid, false);
      storeRef.current.setQuickPrompts(sid, null);
      storeRef.current.setError(sid, null);
    }

    sessionIdRef.current = sid || null;
    setSessionId(sid || null);
    messagesRef.current = welcomeMessages;
    setMessagesState(welcomeMessages);
    setIsTypingState(false);
    setSessionQuickPromptsState(null);
    setErrorState(null);
    setDataSavedToasts([]);
  }, []);

  // 清理 mock 定时器；注意：不在 cleanup 里 abort SSE，
  // 否则 SPA 底部 tab 切换导致组件 unmount/remount 时会中断流。
  useEffect(() => {
    return () => {
      if (replyTimerRef.current) clearTimeout(replyTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      // 组件卸载时清理 isTyping，防止 remount 后残留 false typing indicator
      const sid = sessionIdRef.current;
      if (sid) {
        storeRef.current.setIsTyping(sid, false);
      }
    };
  }, []);

  // 页面真正关闭/刷新时才 abort，避免后台 tab 被误杀
  useEffect(() => {
    const onBeforeUnload = () => {
      abortControllersRef.current.forEach((ctrl) => ctrl.abort());
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const stopGeneration = useCallback(() => {
    abortControllersRef.current.forEach((ctrl) => ctrl.abort());
    abortControllersRef.current.clear();
  }, []);

  // 当传入的 initialSessionId 改变时，同步 sessionId 并从 store 恢复状态
  useEffect(() => {
    const newSessionId = initialSessionId || null;
    setSessionId(newSessionId);

    // 从全局 store 恢复状态，避免切换页面后消息丢失
    if (newSessionId) {
      const storedMessages = storeRef.current.getMessages(newSessionId);
      const storedTyping = storeRef.current.getIsTyping(newSessionId);
      const storedPrompts = storeRef.current.getQuickPrompts(newSessionId);
      const storedError = storeRef.current.getError(newSessionId);
      messagesRef.current = storedMessages;
      setMessagesState(storedMessages);
      setIsTypingState(storedTyping);
      setSessionQuickPromptsState(storedPrompts);
      setErrorState(storedError);
    } else {
      // 无 sessionId 时重置为 welcome
      const welcomeMessages = storeRef.current.getMessages(null);
      messagesRef.current = welcomeMessages;
      setMessagesState(welcomeMessages);
      setIsTypingState(false);
      setSessionQuickPromptsState(null);
      setErrorState(null);
    }
  }, [initialSessionId]);

  // 内容去重键：role + text + 附件数（backend id 与前端本地 id 不一致）
  const msgKey = (m: ChatMessage) =>
    `${m.role}::${m.text ?? ''}::${m.attachments?.length ?? 0}`;

  // 加载历史记录：后端为权威来源，保留前端正在进行中的流式消息
  const refreshHistory = useCallback(async () => {
    if (useMock || !sessionIdRef.current) return;
    if (refreshingRef.current) return;
    // 节流：2 秒内不重复刷新（防止并发 sendMessage 完成后刷屏请求）
    const now = Date.now();
    if (now - lastRefreshRef.current < 2000) return;
    lastRefreshRef.current = now;
    refreshingRef.current = true;
    const sid = sessionIdRef.current;
    try {
      const history = await chatApi.getMessages(sid);
      if (history.length === 0) return;

      const currentMessages = messagesRef.current;
      const currentByKey = new Map(currentMessages.map((m) => [msgKey(m), m]));
      const historyWithLocalMeta = history.map((m) => {
        const local = currentByKey.get(msgKey(m));
        if (!m.agentMeta && local?.agentMeta) {
          return { ...m, agentMeta: local.agentMeta };
        }
        return m;
      });
      const backendKeys = new Set(historyWithLocalMeta.map(msgKey));

      // 保留前端独有的消息：
      // 1. 正在流式中的 AI 占位（有 status 但 text 为空，或属于活跃请求）
      // 2. 当前轮刚生成但后端还没同步的 AI 文本/兜底消息
      // 3. 还没被后端同步的最近用户消息（backendKeys 中没有对应内容）
      const localOnly = currentMessages.filter(
        (m) =>
          !backendKeys.has(msgKey(m)) &&
          ((m.role === "user" && isRecentLocalMessage(m, now)) ||
            (m.role === "ai" &&
              m.id !== "welcome" &&
              (activeInvocationsRef.current.has(m.id) ||
                Boolean(m.status) ||
                isRecentLocalMessage(m, now)))),
      );

      const merged = [...historyWithLocalMeta, ...localOnly];
      if (isMountedRef.current) {
        messagesRef.current = merged;
        setMessagesState(merged);
        storeRef.current.setMessages(sid, merged);
      }
    } catch (e: any) {
      // 429 静默忽略，其他错误正常输出
      if (e?.status !== 429) {
        console.error("Failed to refresh chat history", e);
      }
    } finally {
      refreshingRef.current = false;
    }
  }, [useMock]);

  useEffect(() => {
    if (useMock || !initialSessionId) return;
    refreshHistory();
  }, [initialSessionId, useMock, refreshHistory]);

  // 标签页切回时刷新历史，解决多浏览器/多标签不同步问题
  useEffect(() => {
    if (useMock || !initialSessionId) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshHistory();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [initialSessionId, useMock, refreshHistory]);

  // 确保有 session（真实模式）
  const ensureSession = useCallback(async () => {
    if (useMock) return "mock-session";
    if (sessionId) return sessionId;
    try {
      const session = await chatApi.createSession();
      setSessionId(session.id);
      return session.id;
    } catch {
      return null;
    }
  }, [useMock, sessionId]);

  const sendMessage = useCallback(
    async (text: string, attachments?: Attachment[]) => {
      if (!text.trim() && !attachments?.length) return;
      sendingCountRef.current++;
      setIsTyping(true);

      // 添加用户消息（用唯一 ID 去重 + 内容去重）
      const msgId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const sentAt = nowIso();
      const userMsg: ChatMessage = {
        id: msgId,
        role: "user",
        text: text.trim(),
        time: "刚刚",
        createdAt: sentAt,
        attachments,
      };
      setMessages((prev) => {
        // 防止 StrictMode 等场景下同一条消息被添加两次
        if (prev.some((m) => m.id === msgId)) return prev;
        // 防御：如果最近一条消息已经是相同内容的用户消息（刚刚发送），不再添加
        const lastMsg = prev[prev.length - 1];
        if (
          lastMsg?.role === "user" &&
          lastMsg.text === text.trim() &&
          lastMsg.time === "刚刚"
        ) {
          return prev;
        }
        return [...prev, userMsg];
      });

      if (useMock) {
        // Mock 模式：延迟回复
        if (replyTimerRef.current) clearTimeout(replyTimerRef.current);
        replyTimerRef.current = setTimeout(() => {
          const aiMsg: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: "ai",
            text: getDefaultReply(text.trim()),
            time: "刚刚",
            createdAt: nowIso(),
          };
          setMessages((prev) => [...prev, aiMsg]);
          sendingCountRef.current--;
          if (sendingCountRef.current <= 0) {
            setIsTyping(false);
            sendingCountRef.current = 0;
          }
          replyTimerRef.current = null;
        }, 1200);
      } else {
        // 真实模式：SSE 流式回复 + 前端打字机动画
        // ────────────────────────────────────────────────────────
        // 由于 LLM 推理模型（如 qwen3.5-plus）存在深度思考阶段，
        // 实际 token 会在思考结束后瞬间全部输出，TCP 合并后浏览器
        // 一次性交付给 ReadableStream。因此前端需要自行实现打字机
        // 动画，保证无论网络 chunk 如何到达，UX 都是流畅的逐字展示。
        // ────────────────────────────────────────────────────────
        // 这些变量需在 try 外声明，catch/finally 中才能访问
        let fullText = "";
        let revealedLen = 0;
        let rafId = 0;
        let flushRafId = 0;
        let aiMsgId = "";
        let abortKey = "";
        let hasTextStarted = false;
        let isMerged = false;
        let shouldRefreshHistory = true;

        try {
          const sid = await ensureSession();
          if (!sid) throw new Error("无法创建会话");

          const CHARS_PER_FRAME = 5; // 每帧展示字符数 (~300字/秒@60fps)

          aiMsgId = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          activeInvocationsRef.current.add(aiMsgId);
          setMessages((prev) => [
            ...prev.filter(
              (m) => !(m.role === "ai" && !m.text && !activeInvocationsRef.current.has(m.id)),
            ),
            { id: aiMsgId, role: "ai", text: "", time: "刚刚", createdAt: nowIso() },
          ]);

          // 打字机核心：每帧推进 N 个字符
          const tick = () => {
            if (revealedLen < fullText.length) {
              revealedLen = Math.min(
                fullText.length,
                revealedLen + CHARS_PER_FRAME,
              );
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMsgId
                    ? {
                        ...m,
                        text: fullText.substring(0, revealedLen),
                        status: undefined,
                      }
                    : m,
                ),
              );
              rafId = requestAnimationFrame(tick);
            } else {
              rafId = 0; // 追上了，暂停；新数据到达时会重启
            }
          };

          const controller = new AbortController();
          abortKey = `inv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          abortControllersRef.current.set(abortKey, controller);

          // 消费 SSE 事件：只累积文本，由打字机动画驱动 UI 更新
          for await (const event of chatApi.sendMessage(
            sid,
            text.trim(),
            attachments,
            controller.signal,
          )) {
            if (event.data) {
              try {
                const parsed = JSON.parse(event.data);
                if (parsed.type === "text" && parsed.data) {
                  fullText += parsed.data;
                  // 第一次收到 text 时立即清除 status，避免 PipelineProgress 残留到打字机启动
                  if (!hasTextStarted) {
                    hasTextStarted = true;
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === aiMsgId ? { ...m, status: undefined } : m,
                      ),
                    );
                  }
                  if (!rafId) rafId = requestAnimationFrame(tick);
                } else if (parsed.type === "thinking" && parsed.data?.status) {
                  const statusText = formatThinkingStatus(parsed.data.status);
                  if (!statusText) continue;
                  // 思考状态立即显示（不走打字机）
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === aiMsgId
                        ? {
                            ...m,
                            status: {
                              status: statusText,
                              phase: parsed.data.phase,
                              step: parsed.data.step,
                              progress: parsed.data.progress,
                            },
                          }
                        : m,
                    ),
                  );
                } else if (parsed.type === "done") {
                  const agentMeta = readDoneMeta(parsed.data);
                  if (hasReportTaskSignal(parsed.data)) {
                    markRealDataSeed();
                  }
                  if (agentMeta?.quickPrompts) {
                    setSessionQuickPrompts(agentMeta.quickPrompts);
                  }
                  if (agentMeta) {
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === aiMsgId ? { ...m, agentMeta } : m,
                      ),
                    );
                  }
                } else if (parsed.type === "dataSaved") {
                  const toast: DataSavedToast = {
                    dataType: parsed.data?.dataType || "",
                    key: parsed.data?.key || "",
                    detail: parsed.data?.detail,
                  };
                  if (toast.dataType) {
                    if (shouldClearSampleDataForSavedData(toast)) {
                      markRealDataSeed();
                    }
                    setDataSavedToasts((prev) => {
                      // Deduplicate: skip if same key+type already pending
                      if (prev.some((t) => t.dataType === toast.dataType && t.key === toast.key)) {
                        return prev;
                      }
                      return [...prev, toast];
                    });
                    // Auto-clear after 2.5s
                    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
                    toastTimerRef.current = setTimeout(() => {
                      setDataSavedToasts((prev) => prev.slice(1));
                    }, 2500);
                  }
                } else if (hasReportTaskSignal(parsed)) {
                  markRealDataSeed();
                } else if (parsed.type === "error") {
                  const errCode = readStreamErrorCode(parsed.data);
                  if (errCode === MERGED_ERROR_CODE) {
                    // 该请求被后端 merge，静默停止（由主请求继续展示合并回复）
                    isMerged = true;
                    if (rafId) {
                      cancelAnimationFrame(rafId);
                      rafId = 0;
                    }
                  } else {
                    throw new Error(readStreamErrorMessage(parsed.data));
                  }
                }
              } catch (err) {
                if (!(err instanceof SyntaxError)) {
                  throw err;
                }
                // 非 JSON，直接追加
                fullText += event.data;
                if (!rafId) rafId = requestAnimationFrame(tick);
              }
            }
          }

          if (isMerged && !fullText && aiMsgId) {
            setMessages((prev) =>
              prev.filter((m) => !(m.id === aiMsgId && !m.text)),
            );
          }

          if (!isMerged && !fullText.trim()) {
            throw new Error(EMPTY_STREAM_TEXT);
          }

          // SSE 流结束 — 等打字机展示完毕
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = 0;
          }

          // 被 merge 的请求不执行 flush，避免覆盖主请求的打字机内容
          if (!isMerged) {
            await new Promise<void>((resolve) => {
              const flush = () => {
                if (revealedLen >= fullText.length) {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === aiMsgId
                        ? { ...m, text: fullText, status: undefined }
                        : m,
                    ),
                  );
                  resolve();
                } else {
                  revealedLen = Math.min(
                    fullText.length,
                    revealedLen + CHARS_PER_FRAME,
                  );
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === aiMsgId
                        ? {
                            ...m,
                            text: fullText.substring(0, revealedLen),
                            status: undefined,
                          }
                        : m,
                    ),
                  );
                  flushRafId = requestAnimationFrame(flush);
                }
              };
              flushRafId = requestAnimationFrame(flush);
            });
          }
        } catch (e: any) {
          shouldRefreshHistory = false;
          if (e.name === "AbortError") {
            setError(null); // 用户主动取消不算错误
            // 保留已收到的部分回复，避免切换 tab 后内容丢失
            if (fullText && aiMsgId) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMsgId
                    ? { ...m, text: fullText, status: undefined }
                    : m,
                ),
              );
            } else if (aiMsgId) {
              setMessages((prev) =>
                prev.filter((m) => !(m.id === aiMsgId && !m.text)),
              );
            }
          } else {
            // SSE 失败，降级提示 + 持久化错误状态
            const errText = e?.message === EMPTY_STREAM_TEXT ? EMPTY_STREAM_TEXT : STREAM_ERROR_TEXT;
            if (fullText.trim() && aiMsgId) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMsgId
                    ? { ...m, text: fullText, status: undefined }
                    : m,
                ),
              );
              setError(null);
            } else if (aiMsgId) {
              // 复用占位消息，清除 status 避免 typing indicator 残留
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMsgId
                    ? { ...m, text: errText, status: undefined }
                    : m,
                  ),
              );
              setError(errText);
            } else {
              const aiMsg: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: "ai",
                text: errText,
                time: "刚刚",
                createdAt: nowIso(),
              };
              setMessages((prev) => [...prev, aiMsg]);
              setError(errText);
            }
          }
        } finally {
          if (rafId) cancelAnimationFrame(rafId);
          if (flushRafId) cancelAnimationFrame(flushRafId);
          sendingCountRef.current--;
          if (sendingCountRef.current <= 0) {
            setIsTyping(false);
            sendingCountRef.current = 0;
          }
          abortControllersRef.current.delete(abortKey);
          activeInvocationsRef.current.delete(aiMsgId);
        }
        // SSE 结束后静默刷新历史，确保多标签/多浏览器同步
        // 被 merge 的请求由主请求负责刷新，避免重复请求导致 429
        if (!isMerged && shouldRefreshHistory) {
          refreshHistory();
        }
      }
    },
    [useMock, ensureSession, refreshHistory, markRealDataSeed],
  );

  const dismissDataSavedToast = useCallback((index: number) => {
    setDataSavedToasts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const submitFeedback = useCallback(async (
    messageId: string,
    rating: AgentFeedbackRating,
    rememberScope: AgentRememberScope = "remember",
  ) => {
    const sid = sessionIdRef.current;
    if (!sid) {
      throw new Error("会话不存在");
    }

    const currentMessage = messagesRef.current.find((message) => message.id === messageId);
    const backendMessageId = currentMessage?.agentMeta?.agent?.assistantMessageId || messageId;
    const previousMeta = currentMessage?.agentMeta;
    const optimisticFeedback = {
      rating,
      submittedAt: nowIso(),
    };

    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              agentMeta: {
                ...(message.agentMeta ?? {}),
                feedback: optimisticFeedback,
              },
            }
          : message,
      ),
    );

    try {
      const result = await chatApi.submitFeedback(sid, backendMessageId, rating, { rememberScope });
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                agentMeta: {
                  ...(message.agentMeta ?? {}),
                  feedback: result.feedback ?? optimisticFeedback,
                },
              }
            : message,
        ),
      );
    } catch (error) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                agentMeta: previousMeta,
              }
            : message,
        ),
      );
      throw error;
    }
  }, [setMessages]);

  const rewriteMessage = useCallback(async (
    messageId: string,
    target: AgentRewriteTarget,
    rememberScope: AgentRememberScope = "remember",
  ) => {
    const sid = sessionIdRef.current;
    if (!sid) {
      throw new Error("会话不存在");
    }

    const currentMessage = messagesRef.current.find((message) => message.id === messageId);
    const backendMessageId = currentMessage?.agentMeta?.agent?.assistantMessageId || messageId;
    const previousText = currentMessage?.text;

    try {
      const result = await chatApi.rewriteMessage(sid, backendMessageId, target, rememberScope);
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                text: result.content,
              }
            : message,
        ),
      );
    } catch (error) {
      if (previousText !== undefined) {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === messageId ? { ...message, text: previousText } : message,
          ),
        );
      }
      throw error;
    }
  }, [setMessages]);

  return {
    messages,
    isTyping,
    sendMessage,
    stopGeneration,
    submitFeedback,
    rewriteMessage,
    sessionQuickPrompts,
    error,
    dataSavedToasts,
    dismissDataSavedToast,
    refreshHistory,
    resetSessionState,
  };
}
